/**
 * Helpers for working with the new `UIMetadata` rows returned by
 * `/api/broker_deals/metadata_v2?table_name=...`.
 *
 * The Phase-1 `UIMetadata` row shape differs from the legacy
 * `/broker_deals/metadata` shape: it has a richer `data_type`
 * (`Text|Numeric|Date|Boolean|Lookup|LongText|Timestamp`) and adds
 * `input_type` / `category` / `is_pii` / `distinct_values_lookup`.
 *
 * Keeping the pipeline isolated here means `dealsList.jsx` and
 * `dealDetail/index.jsx` agree on the rendering rules without duplicating
 * the data-type switch.
 */

import { numberFormatter } from "@warwick-investment-group/wig-react-library";

import { formatDateOnly, formatTimestamp } from "./dateFormat";

export const titleCase = (snake) =>
  String(snake || "")
    .toLowerCase()
    .split("_")
    .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");

const NUMERIC_TYPES = new Set(["Numeric"]);
const DATE_TYPES = new Set(["Date", "Timestamp"]);

export const isNumericType = (dataType) => NUMERIC_TYPES.has(dataType);
export const isDateType = (dataType) => DATE_TYPES.has(dataType);

/**
 * Map v2 metadata rows -> AntD ColumnProps for the deals grid. Mirrors
 * the shape used by the legacy `metadataToColumns` in `dealsList.jsx`
 * so `WigAntTable` can render either grid without changes.
 *
 * @param {Array} metadata  — UIMetadata rows from `/metadata_v2`
 * @returns {Array}         — AntD `ColumnProps` array
 */
export const metadataV2ToColumns = (metadata) => {
  if (!Array.isArray(metadata) || metadata.length === 0) return [];
  // The deals grid's metadata is learned on the way past, so that caller does
  // not have to remember to. The child tabs build their columns with the
  // LIBRARY's `metadataToAntdColumns` and never reach here, which is why the
  // two helpers below take metadata of their own.
  learnFieldFormats(metadata);
  const sorted = [...metadata].sort(
    (a, b) => (a.ord ?? 9999) - (b.ord ?? 9999)
  );
  return sorted
    .filter((m) => !m.hide)
    .map((m) => {
      const numeric = isNumericType(m.data_type);
      const date = isDateType(m.data_type);
      const col = {
        title: m.label && m.label !== m.field ? m.label : titleCase(m.field),
        dataIndex: m.field,
        key: m.field,
        width: numeric ? 120 : 160,
        align: numeric ? "right" : undefined,
      };
      // Whether this column offers a filter dropdown is a property of the
      // FIELD, set on Admin -> Field Settings (de-hub migration 094). Same
      // resolution the library uses: free text defaults to no dropdown
      // because its distinct values are one per row, and an explicit setting
      // wins either way. Left undefined otherwise, so WigAntTable's own
      // default still applies.
      if (m.data_type === "LongText") col.filterable = false;
      if (m.filterable !== null && m.filterable !== undefined) {
        col.filterable = m.filterable;
      }
      if (numeric) {
        col.sorter = (a, b) =>
          Number(a[m.field] ?? 0) - Number(b[m.field] ?? 0);
        col.render = (value) =>
          value == null || value === "" ? "" : fmtNumber(value, m.field);
      } else if (date) {
        col.sorter = (a, b) =>
          String(a[m.field] ?? "").localeCompare(String(b[m.field] ?? ""));
      } else {
        col.sorter = (a, b) =>
          String(a[m.field] ?? "").localeCompare(String(b[m.field] ?? ""));
      }
      return col;
    });
};

/**
 * Group `UIMetadata` rows by their `category` field, preserving `ord`
 * order inside each group and falling back to "Other" when category is
 * empty. Used by the Deal Detail "Overview" tab.
 *
 * @param {Array} metadata
 * @returns {Array<[string, Array]>} — ordered [category, fields[]] pairs
 */
export const groupMetadataByCategory = (metadata) => {
  const safe = Array.isArray(metadata) ? metadata : [];
  const groups = new Map();
  for (const m of safe) {
    if (!m || m.hide) continue;
    const cat = m.category || "Other";
    if (!groups.has(cat)) groups.set(cat, []);
    groups.get(cat).push(m);
  }
  for (const [, fields] of groups) {
    fields.sort((a, b) => (a.ord ?? 9999) - (b.ord ?? 9999));
  }
  // Categories ordered by the smallest `ord` they contain so the user
  // sees them in the same logical sequence as the columns in the grid.
  return Array.from(groups.entries()).sort((a, b) => {
    const aMin = Math.min(...a[1].map((f) => f.ord ?? 9999));
    const bMin = Math.min(...b[1].map((f) => f.ord ?? 9999));
    return aMin - bMin;
  });
};

const MUTED = "rgba(0,0,0,0.35)";

// A Date value must not round-trip through `new Date(...)` — see
// `formatDateOnly` for why doing so silently renders the previous day.
const fmtDate = formatDateOnly;

const fmtTimestamp = formatTimestamp;

// What each FORMAT NAME means (money.ledger, money.exact, money.rate, acres,
// interest, percent) lives in wig-react-library's numberFormat, shared with
// the report components: `numberFormatter(name)` gives the formatter, or null
// for an unknown name. The split, and it is the point of #189: WHICH format a
// field uses is DATA — a row edit, no release. What a NAME means is code.

// `field -> format name`, learned from metadata as it is fetched.
//
// WHY A MODULE-SCOPED REGISTRY AND NOT A THREADED ARGUMENT
// `fmtNumber` is reached from six call sites across four deal-detail tabs,
// two of which (`applyNumericFormatters`, `numericCellRenderers`) receive
// columns rather than metadata and have no row to read. Threading `format`
// through all of them is the explicit, stateless design — and it means
// touching the highest-traffic screens in the app for a change whose visible
// effect is zero. The risk was out of proportion to the benefit.
//
// The cost of this choice is staleness: change a field's format and a tab
// that is already open keeps the old one until its next metadata fetch. For
// display formatting that is acceptable; it would not be for a permission.
const FIELD_FORMATS = new Map();

/**
 * Register the formats in a metadata array. Safe to call repeatedly and with
 * partial metadata — each table's rows teach it about their own fields.
 *
 * Keyed on field name, not (table, field), matching what the callers can
 * supply. Two tables sharing a field name share its format, which is true of
 * every pair we have (`gross_acres` is acreage on both tables that carry it).
 * Revisit if that stops being true.
 */
export const learnFieldFormats = (metadata) => {
  for (const m of Array.isArray(metadata) ? metadata : []) {
    if (m?.field && typeof m.format === "string" && m.format) {
      FIELD_FORMATS.set(m.field, m.format);
    }
  }
};

/** Test seam: forget everything learned. */
export const resetFieldFormats = () => FIELD_FORMATS.clear();

/**
 * The formatter for a field, or null for "render it as a plain number".
 *
 * Metadata wins; the hardcoded map below is the fallback for a de-hub that
 * predates migration 092. An unknown format NAME falls through to plain too —
 * an older build reading newer data must not throw.
 */
const formatterFor = (field) => {
  if (!field) return null;
  const named = FIELD_FORMATS.get(field);
  if (named) return numberFormatter(named);
  return LEGACY_FIELD_FORMATTERS[field] ?? null;
};

// FALLBACK ONLY — superseded by the `format` column (de-hub migration 092).
// Kept so a client running against a de-hub without 092 renders as it always
// did, rather than dropping every number to a plain `toLocaleString`. Do not
// add to this; set `format` on the metadata row instead.
const LEGACY_FIELD_FORMATTERS = {
  // Money — lump sums (whole dollar).
  total_bonus: numberFormatter("money.ledger"),
  lease_cost: numberFormatter("money.ledger"),
  allocated_value: numberFormatter("money.ledger"),
  // A payment is a cheque someone actually wrote, so it shows cents. It sat
  // with the lump sums above until feedback #72: $439.66 was entered, saved
  // correctly as 439.66, and then displayed back as "$440" — which reads as
  // the app having altered the amount. The other three are genuinely rounded
  // ledger totals (UAT #30/#31/#27); only this one is a literal transaction.
  payment_amount: numberFormatter("money.exact"),
  // Money — per-acre rates (2 decimals).
  bonus_per_acre: numberFormatter("money.rate"),
  extension_option_bpa: numberFormatter("money.rate"),
  // Acreage — up to 6 decimals.
  deal_net_acres_delivered: numberFormatter("acres"),
  gross_acres: numberFormatter("acres"),
  co_net_acres_delivered: numberFormatter("acres"),
  // Interest fractions — decimal 0–1 at 8 decimals.
  delivered_nri: numberFormatter("interest"),
  nri: numberFormatter("interest"),
  wi: numberFormatter("interest"),
  allocation: numberFormatter("interest"),
  lessor_royalty: numberFormatter("interest"),
  other_burdens: numberFormatter("interest"),
  warwick_orri: numberFormatter("interest"),
};

export const fmtNumber = (v, field) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  const special = formatterFor(field);
  if (special) return special(n);
  return n.toLocaleString();
};

// Attach the field-aware numeric formatter to an AntD column array built by the
// library's `metadataToAntdColumns` (which doesn't know our formats). Used by
// the child-entity tabs (Lease/Deeds, Wells) whose grids come from the library.
export const applyNumericFormatters = (cols, metadata) => {
  // The rows these columns were built from, so a `format` set on them is
  // honoured here too. Optional: without it the legacy map still applies.
  if (metadata) learnFieldFormats(metadata);
  return (Array.isArray(cols) ? cols : []).map((c) => {
    const field = c?.dataIndex ?? c?.key;
    if (field && formatterFor(field)) {
      return {
        ...c,
        align: "right",
        render: (v) => (v == null || v === "" ? "" : fmtNumber(v, field)),
      };
    }
    return c;
  });
};

// Field-keyed cell renderers for `WigMetadataCrudPanel` (tract/payment sub-
// panels build their own columns internally but honor a `cellRenderers` map).
// The library passes a `{ value, data, index }` params object.
export const numericCellRenderers = (metadata) => {
  if (metadata) learnFieldFormats(metadata);
  const out = {};
  // Both sources: what metadata has taught us, plus the legacy fallback.
  const fields = new Set([
    ...FIELD_FORMATS.keys(),
    ...Object.keys(LEGACY_FIELD_FORMATTERS),
  ]);
  for (const field of fields) {
    out[field] = ({ value }) =>
      value == null || value === "" ? "" : fmtNumber(value, field);
  }
  return out;
};

/**
 * Format a raw value for the Deal Detail Overview tab. Returns either a
 * string or a tiny placeholder object — callers wrap as needed.
 *
 * - Date / Timestamp -> formatted date
 * - Numeric         -> toLocaleString
 * - Boolean         -> "Yes" / "No"
 * - Lookup          -> raw id (Phase 2 resolves to label)
 * - LongText        -> string, no truncation
 * - Text (default)  -> string
 *
 * @param {object} meta   — the UIMetadata row
 * @param {*}      value  — the raw value off the deal row
 * @returns {{ kind: "blank" } | { kind: "text", text: string } | { kind: "longtext", text: string }}
 */
export const formatV2Value = (meta, value) => {
  if (value === null || value === undefined || value === "") {
    return { kind: "blank" };
  }
  const dt = meta?.data_type;
  if (dt === "Timestamp") {
    const s = fmtTimestamp(value);
    return s == null ? { kind: "blank" } : { kind: "text", text: s };
  }
  if (dt === "Date") {
    const s = fmtDate(value);
    return s == null ? { kind: "blank" } : { kind: "text", text: s };
  }
  if (dt === "Boolean") {
    if (value === true || value === 1 || value === "1" || value === "true")
      return { kind: "text", text: "Yes" };
    if (value === false || value === 0 || value === "0" || value === "false")
      return { kind: "text", text: "No" };
    return { kind: "text", text: String(value) };
  }
  if (dt === "Numeric") {
    // Learn this row's format first: the deal Overview renders through here
    // without ever building grid columns, so on a deep link to a deal the
    // registry would otherwise be empty and a money.rate field (promote,
    // feedback #37) would show as a bare number. Same as mapPopup.
    learnFieldFormats([meta]);
    return { kind: "text", text: fmtNumber(value, meta?.field) };
  }
  if (dt === "Lookup") {
    // Phase 2 resolves the id -> label via the lookup table named in
    // `distinct_values_lookup`. For now show the raw id.
    return { kind: "text", text: String(value) };
  }
  if (dt === "LongText") {
    return { kind: "longtext", text: String(value) };
  }
  return { kind: "text", text: String(value) };
};

export const MUTED_STYLE = { color: MUTED };

/**
 * Map v2 metadata rows -> AG Grid `ColDef[]`. Used by the deal-side
 * child-entity tabs (EntitiesTab, StatusHistoryTab, ApprovalsTab,
 * AssignmentRecordingsTab) so they can drive their grids straight off the
 * /metadata_v2 envelope.
 *
 * - Numeric  -> right-aligned, numeric filter, toLocaleString formatter
 * - Date     -> mm/dd/yyyy formatter, date filter
 * - Timestamp-> locale string formatter
 * - Boolean  -> "Yes"/"No"
 * - Lookup   -> raw id (callers can wrap with cellRenderer for label resolution)
 * - LongText -> tooltipValueGetter populated with full value
 *
 * @param {Array}  metadata  — UIMetadata rows from /metadata_v2
 * @param {object} [options]
 * @param {Array<string>} [options.hideFields] — field names to suppress (e.g. server-managed audit cols)
 */
export const metadataToColumns = (metadata, options = {}) => {
  if (!Array.isArray(metadata) || metadata.length === 0) return [];
  const hideSet = new Set(options.hideFields || []);
  const sorted = [...metadata].sort(
    (a, b) => (a.ord ?? 9999) - (b.ord ?? 9999)
  );
  return sorted
    .filter((m) => !m.hide && !hideSet.has(m.field))
    .map((m) => {
      const dt = m.data_type;
      const numeric = isNumericType(dt);
      const date = isDateType(dt);
      const col = {
        field: m.field,
        headerName: m.label && m.label !== m.field ? m.label : titleCase(m.field),
        sortable: true,
        resizable: true,
        filter: true,
        width: numeric ? 130 : 170,
      };
      if (numeric) {
        col.type = "rightAligned";
        col.filter = "agNumberColumnFilter";
        col.valueFormatter = (p) =>
          p.value == null || p.value === "" ? "" : fmtNumber(p.value, m.field);
      } else if (date) {
        col.filter = "agDateColumnFilter";
        col.valueFormatter = (p) => {
          if (p.value == null || p.value === "") return "";
          return dt === "Timestamp" ? fmtTimestamp(p.value) : fmtDate(p.value);
        };
      } else if (dt === "Boolean") {
        col.valueFormatter = (p) => {
          const v = p.value;
          if (v === true || v === 1 || v === "1" || v === "true") return "Yes";
          if (v === false || v === 0 || v === "0" || v === "false") return "No";
          return v == null ? "" : String(v);
        };
      } else if (dt === "LongText") {
        col.tooltipValueGetter = (p) => p.value;
        col.width = 240;
      }
      return col;
    });
};

/**
 * Pick the AntD input component family for an editor based on metadata.
 * Returns a small descriptor consumed by the per-tab modal forms — keeps
 * the component-by-component switch statement out of every tab file.
 *
 * The contract: { kind, props? } where kind is one of
 *   "text" | "longtext" | "number" | "date" | "timestamp" | "boolean" |
 *   "select" | "lookup"
 *
 * Values respect `input_type` from the metadata when present, else fall
 * back to `data_type`.
 */
export const fieldEditorDescriptor = (meta) => {
  const it = (meta?.input_type || "").toLowerCase();
  const dt = meta?.data_type;
  if (it === "textarea" || dt === "LongText") return { kind: "longtext" };
  if (it === "select" || dt === "Lookup") {
    // Lookup fields backed by a named lookup table become async typeaheads;
    // plain enum selects (no lookup table) become static dropdowns.
    if (meta?.distinct_values_lookup) {
      return { kind: "lookup", lookup: meta.distinct_values_lookup };
    }
    return { kind: "select", options: meta?.options || [] };
  }
  if (it === "checkbox" || dt === "Boolean") return { kind: "boolean" };
  if (dt === "Numeric" || it === "number") return { kind: "number" };
  if (dt === "Timestamp") return { kind: "timestamp" };
  if (dt === "Date" || it === "date") return { kind: "date" };
  return { kind: "text" };
};

